import { useState } from 'react';
import { Plus, Trash2, Loader2, Pencil, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  useEstablishmentQuestions,
  EstablishmentQuestionInput,
} from '@/hooks/useEstablishmentQuestions';
import {
  EstablishmentQuestion,
  QuestionScope,
  QUESTION_MAX_LENGTH,
  QUESTION_MAX_OPTIONS,
  QUESTION_OPTION_MAX_LENGTH,
} from '@/lib/establishmentQuestionUtils';
import { WEEKDAY_LABELS } from '@/lib/establishmentNoticeUtils';
import { EstablishmentQuestionField } from '@/components/EstablishmentQuestionField';
import { formatDateLocal, parseDateLocal } from '@/lib/timeFormatUtils';

interface EstablishmentQuestionsSettingsProps {
  establishmentId?: string | null;
}

const emptyForm: EstablishmentQuestionInput = {
  question: '',
  options: ['', ''],
  allowMultiple: false,
  isRequired: false,
  scope: 'permanent',
  specificDate: null,
  weekday: null,
  isActive: true,
};

function describePeriod(question: EstablishmentQuestion): string {
  if (question.scope === 'date' && question.specificDate) {
    return parseDateLocal(question.specificDate).toLocaleDateString('fr-CH', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  }
  if (question.scope === 'weekday' && question.weekday !== null) {
    return `Tous les ${WEEKDAY_LABELS[question.weekday].toLowerCase()}s`;
  }
  return 'Affichage permanent';
}

export function EstablishmentQuestionsSettings({ establishmentId }: EstablishmentQuestionsSettingsProps) {
  const { questions, isLoading, createQuestion, updateQuestion, deleteQuestion, isSaving } =
    useEstablishmentQuestions({ establishmentId });

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<EstablishmentQuestionInput>(emptyForm);
  const [previewAnswers, setPreviewAnswers] = useState<string[]>([]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setPreviewAnswers([]);
    setIsFormOpen(true);
  };

  const openEdit = (question: EstablishmentQuestion) => {
    setEditingId(question.id);
    setForm({
      question: question.question,
      options: question.options.length > 0 ? question.options : ['', ''],
      allowMultiple: question.allowMultiple,
      isRequired: question.isRequired,
      scope: question.scope,
      specificDate: question.specificDate,
      weekday: question.weekday,
      isActive: question.isActive,
    });
    setPreviewAnswers([]);
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingId(null);
    setForm(emptyForm);
    setPreviewAnswers([]);
  };

  const cleanedOptions = form.options.map((o) => o.trim()).filter((o) => o !== '');

  const canSave =
    form.question.trim() !== '' &&
    cleanedOptions.length >= 1 &&
    new Set(cleanedOptions).size === cleanedOptions.length &&
    (form.scope !== 'date' || !!form.specificDate) &&
    (form.scope !== 'weekday' || form.weekday !== null);

  const handleSave = async () => {
    if (!canSave) return;
    const payload = { ...form, options: cleanedOptions };
    if (editingId) {
      await updateQuestion({ id: editingId, input: payload });
    } else {
      await createQuestion(payload);
    }
    closeForm();
  };

  const setOption = (index: number, value: string) => {
    setForm((f) => {
      const next = [...f.options];
      next[index] = value.slice(0, QUESTION_OPTION_MAX_LENGTH);
      return { ...f, options: next };
    });
  };

  const previewQuestion: EstablishmentQuestion = {
    id: 'preview',
    establishmentId: establishmentId ?? '',
    question: form.question,
    options: cleanedOptions,
    allowMultiple: form.allowMultiple,
    isRequired: form.isRequired,
    scope: form.scope,
    specificDate: form.specificDate ?? null,
    weekday: form.weekday ?? null,
    isActive: true,
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-0.5">
          <Label className="text-sm font-medium">Question au client</Label>
          <p className="text-xs text-muted-foreground">
            Une question simple posée au moment de la réservation (5 réponses maximum). La réponse
            apparaît dans l'email de notification, dans le détail de la réservation et sur la liste
            imprimée du service.
          </p>
        </div>
        {!isFormOpen && (
          <Button type="button" variant="outline" size="sm" onClick={openCreate}>
            <Plus className="w-4 h-4 mr-1" />
            Ajouter
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          Chargement des questions…
        </div>
      ) : questions.length === 0 && !isFormOpen ? (
        <p className="text-xs text-muted-foreground">
          Aucune question. La fonctionnalité reste invisible pour vos clients tant qu'aucune
          question active n'existe.
        </p>
      ) : (
        <div className="space-y-2">
          {questions.map((question) => (
            <div
              key={question.id}
              className="flex items-start justify-between gap-3 rounded-lg border p-3"
            >
              <div className="min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    {describePeriod(question)}
                  </Badge>
                  <Badge variant="secondary" className="text-xs">
                    {question.allowMultiple ? 'Réponses multiples' : 'Réponse unique'}
                  </Badge>
                  {question.isRequired && (
                    <Badge variant="secondary" className="text-xs">
                      Obligatoire
                    </Badge>
                  )}
                  {!question.isActive && (
                    <Badge variant="secondary" className="text-xs">
                      Désactivée
                    </Badge>
                  )}
                </div>
                <p className="text-sm break-words">{question.question}</p>
                <p className="text-xs text-muted-foreground break-words">
                  {question.options.join(' · ')}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Switch
                  checked={question.isActive}
                  disabled={isSaving}
                  onCheckedChange={(checked) =>
                    updateQuestion({
                      id: question.id,
                      input: {
                        question: question.question,
                        options: question.options,
                        allowMultiple: question.allowMultiple,
                        isRequired: question.isRequired,
                        scope: question.scope,
                        specificDate: question.specificDate,
                        weekday: question.weekday,
                        isActive: checked,
                      },
                    })
                  }
                />
                <Button type="button" variant="ghost" size="icon" onClick={() => openEdit(question)}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={isSaving}
                  onClick={() => deleteQuestion(question.id)}
                >
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {isFormOpen && (
        <div className="space-y-4 rounded-lg border p-4">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">
              {editingId ? 'Modifier la question' : 'Nouvelle question'}
            </Label>
            <Button type="button" variant="ghost" size="icon" onClick={closeForm}>
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="space-y-2">
            <Label className="text-sm" htmlFor="question-text">
              Question
            </Label>
            <Input
              id="question-text"
              value={form.question}
              onChange={(e) =>
                setForm((f) => ({ ...f, question: e.target.value.slice(0, QUESTION_MAX_LENGTH) }))
              }
              placeholder="Ex : Souhaitez-vous le menu de la fête des mères ?"
            />
            <div className="flex justify-end">
              <span className="text-xs text-muted-foreground">
                {form.question.length}/{QUESTION_MAX_LENGTH}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm">Réponses proposées ({QUESTION_MAX_OPTIONS} maximum)</Label>
            {form.options.map((option, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  value={option}
                  onChange={(e) => setOption(index, e.target.value)}
                  placeholder={`Réponse ${index + 1}`}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={form.options.length <= 1}
                  onClick={() =>
                    setForm((f) => ({ ...f, options: f.options.filter((_, i) => i !== index) }))
                  }
                >
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            ))}
            {form.options.length < QUESTION_MAX_OPTIONS && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setForm((f) => ({ ...f, options: [...f.options, ''] }))}
              >
                <Plus className="w-4 h-4 mr-1" />
                Ajouter une réponse
              </Button>
            )}
          </div>

          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <Label htmlFor="question-multiple" className="text-sm">
                Autoriser plusieurs réponses
              </Label>
              <p className="text-xs text-muted-foreground">
                Sinon, le client ne peut choisir qu'une seule réponse.
              </p>
            </div>
            <Switch
              id="question-multiple"
              checked={form.allowMultiple}
              onCheckedChange={(checked) => {
                setForm((f) => ({ ...f, allowMultiple: checked }));
                setPreviewAnswers([]);
              }}
            />
          </div>

          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <Label htmlFor="question-required" className="text-sm">
                Réponse obligatoire
              </Label>
              <p className="text-xs text-muted-foreground">
                Le client ne peut pas valider sa réservation sans répondre.
              </p>
            </div>
            <Switch
              id="question-required"
              checked={form.isRequired}
              onCheckedChange={(checked) => setForm((f) => ({ ...f, isRequired: checked }))}
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm">Période d'affichage</Label>
            <RadioGroup
              value={form.scope}
              onValueChange={(v) =>
                setForm((f) => ({
                  ...f,
                  scope: v as QuestionScope,
                  specificDate: v === 'date' ? f.specificDate : null,
                  weekday: v === 'weekday' ? f.weekday : null,
                }))
              }
              className="flex flex-col gap-2 sm:flex-row sm:gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="permanent" id="question-scope-permanent" />
                <Label htmlFor="question-scope-permanent" className="text-sm font-normal cursor-pointer">
                  Permanent
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="date" id="question-scope-date" />
                <Label htmlFor="question-scope-date" className="text-sm font-normal cursor-pointer">
                  Une date précise
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="weekday" id="question-scope-weekday" />
                <Label htmlFor="question-scope-weekday" className="text-sm font-normal cursor-pointer">
                  Chaque semaine
                </Label>
              </div>
            </RadioGroup>
          </div>

          {form.scope === 'date' && (
            <div className="space-y-2">
              <Label className="text-sm" htmlFor="question-date">
                Date concernée
              </Label>
              <Input
                id="question-date"
                type="date"
                value={form.specificDate ?? ''}
                min={formatDateLocal(new Date())}
                onChange={(e) => setForm((f) => ({ ...f, specificDate: e.target.value || null }))}
              />
            </div>
          )}

          {form.scope === 'weekday' && (
            <div className="space-y-2">
              <Label className="text-sm">Jour de la semaine</Label>
              <Select
                value={form.weekday !== null && form.weekday !== undefined ? String(form.weekday) : ''}
                onValueChange={(v) => setForm((f) => ({ ...f, weekday: Number(v) }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choisir un jour" />
                </SelectTrigger>
                <SelectContent>
                  {WEEKDAY_LABELS.map((label, index) => (
                    <SelectItem key={label} value={String(index)}>
                      Tous les {label.toLowerCase()}s
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex items-center justify-between gap-4">
            <Label htmlFor="question-active" className="text-sm">
              Question active
            </Label>
            <Switch
              id="question-active"
              checked={form.isActive}
              onCheckedChange={(checked) => setForm((f) => ({ ...f, isActive: checked }))}
            />
          </div>

          {form.question.trim() !== '' && cleanedOptions.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Aperçu client :</Label>
              <EstablishmentQuestionField
                question={previewQuestion}
                answers={previewAnswers}
                onAnswersChange={setPreviewAnswers}
                idPrefix="preview"
              />
            </div>
          )}

          <div className="flex gap-2">
            <Button type="button" onClick={handleSave} disabled={!canSave || isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement…
                </>
              ) : editingId ? (
                'Enregistrer les modifications'
              ) : (
                'Ajouter la question'
              )}
            </Button>
            <Button type="button" variant="outline" onClick={closeForm}>
              Annuler
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
